/**
 * Вода по файлу ирригации на экранах (С4–С5): слова, тона и подписи БЕЗ арифметики.
 *
 * Все числа — готовые поля сервера и слоя метрик `water.*`. Здесь только
 * перевод кодов состояния в слова и вывод значения метрики вместе с её единицей
 * («1 084 672 м³», «89,9 %», «695 пар»): нет числа — «нет данных», а не ноль.
 */
import type { WaterMetric } from '../../../api/client';
import { haUnit, m3, num, pct, windowLabel } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { lazyWords } from '../../lib/lazyWords';
import { tr } from '../../../i18n/runtime';
import type { BadgeTone } from '../../ui/Badge';

export type IrrigationState = 'done' | 'partial' | 'waiting' | 'no_delivery' | 'upcoming';

const m = defineMessages({
  stateDone: { id: 'water.state.done', defaultMessage: 'полито', description: 'Состояние полива контура: событие полива есть в файле ирригации; бейдж и слово в сводке, строчными, коротко' },
  statePartial: { id: 'water.state.partial', defaultMessage: 'полито частично', description: 'Состояние полива контура: полита часть площади; бейдж, строчными, коротко' },
  stateWaiting: { id: 'water.state.waiting', defaultMessage: 'ждём подачи', description: 'Состояние полива: окно полива идёт, события в файле ирригации ещё нет (это не «не полито»); бейдж, коротко' },
  stateNoDelivery: { id: 'water.state.noDelivery', defaultMessage: 'без подачи', description: 'Состояние полива: окно закрылось, события полива в файле ирригации нет; бейдж, коротко' },
  stateUpcoming: { id: 'water.state.upcoming', defaultMessage: 'окно впереди', description: 'Состояние полива: окно полива ещё не открылось; бейдж, коротко' },
  noteDone: { id: 'water.state.note.done', defaultMessage: 'событие полива есть в файле ирригации', description: 'Всплывающая подсказка к бейджу состояния «полито»' },
  notePartial: { id: 'water.state.note.partial', defaultMessage: 'полита часть площади — ждём дополнения', description: 'Всплывающая подсказка к бейджу «полито частично»: файл может дополниться' },
  noteWaiting: { id: 'water.state.note.waiting', defaultMessage: 'окно идёт, файл ирригации может опаздывать — это не «не полито»', description: 'Всплывающая подсказка к бейджу «ждём подачи»: пока окно идёт, отсутствие события не значит, что не полито' },
  noteNoDelivery: { id: 'water.state.note.noDelivery', defaultMessage: 'окно закрылось, события полива в файле нет', description: 'Всплывающая подсказка к бейджу «без подачи»' },
  noteUpcoming: { id: 'water.state.note.upcoming', defaultMessage: 'окно ещё не открылось', description: 'Всплывающая подсказка к бейджу «окно впереди»' },
  noData: { id: 'water.file.noData', defaultMessage: 'нет данных', description: 'Вместо числа метрики воды, когда сервер значение не назвал: это не ноль, а отсутствие данных' },
  pairs: { id: 'water.file.pairs', defaultMessage: '{count, plural, one {# пара} few {# пары} many {# пар} other {# пары}}', description: 'Значение метрики воды в парах «контур × полив»: «12 пар»' },
  perHa: { id: 'water.file.m3PerHa', defaultMessage: '{value} м³/га', description: 'Значение метрики «кубометров воды на гектар»; {value} — число, уже отформатированное' },
  source: {
    id: 'water.file.source',
    defaultMessage: 'файл ирригации за {period}, версия {version}, строка {row}',
    description: 'Источник факта полива одной строкой (подпись мелким шрифтом); {period} — период файла («01–06 окт»), {version} и {row} — номера версии файла и строки в нём',
  },
  disputeNone: { id: 'water.dispute.reasonNone', defaultMessage: 'Полива не было', description: 'Переключатель в форме спора фермера о поливе: полив, отмеченный в файле, не проводился' },
  disputeMissing: { id: 'water.dispute.reasonMissing', defaultMessage: 'Полито, а в файле нет', description: 'Переключатель в форме спора фермера о поливе: полив был, а в файле ирригации его нет' },
});

/** Слова состояния полива: «ждём» и «без подачи» — разные утверждения, не «не полито». */
export const STATE_WORDS: Record<IrrigationState, string> = lazyWords<IrrigationState>({
  done: m.stateDone,
  partial: m.statePartial,
  waiting: m.stateWaiting,
  no_delivery: m.stateNoDelivery,
  upcoming: m.stateUpcoming,
});

export const STATE_NOTES: Record<IrrigationState, string> = lazyWords<IrrigationState>({
  done: m.noteDone,
  partial: m.notePartial,
  waiting: m.noteWaiting,
  no_delivery: m.noteNoDelivery,
  upcoming: m.noteUpcoming,
});

export const STATE_TONES: Record<IrrigationState, BadgeTone> = {
  done: 'aqua',
  partial: 'aqua-soft',
  waiting: 'outline',
  no_delivery: 'copper',
  upcoming: 'outline',
};

/** Порядок показа: от требующего внимания к спокойному. */
export const STATE_ORDER: IrrigationState[] = ['no_delivery', 'waiting', 'partial', 'done', 'upcoming'];

export function isState(value: string): value is IrrigationState {
  return value in STATE_WORDS;
}

/** Значение метрики с единицей слоя; нет числа — «нет данных». */
export function metricText(metric: Pick<WaterMetric, 'value' | 'unit'>): string {
  const value = metric.value;
  if (value === null || value === undefined) return tr(m.noData);
  switch (metric.unit) {
    case '%':
      return pct(value, 1);
    case 'га':
      return haUnit(value);
    case 'м³':
      return m3(value);
    case 'м³/га':
      return tr(m.perHa, { value: num(Math.round(value)) });
    // i18n-ignore: код единицы из ответа сервера
    case 'пар':
      return tr(m.pairs, { count: Math.round(value) });
    default:
      return `${num(Math.round(value))} ${metric.unit}`;
  }
}

export function metricById(metrics: readonly WaterMetric[], id: string): WaterMetric | null {
  return metrics.find((m) => m.id === id) ?? null;
}

/** Файл-источник одной строкой: «файл ирригации за 01–06 окт, версия 1, строка 1». */
export function sourceText(source: {
  period_from: string;
  period_to: string;
  version_no: number;
  row_no: number;
}): string {
  return tr(m.source, { period: windowLabel(source.period_from, source.period_to), version: source.version_no, row: source.row_no });
}

/** Причины спора фермера — две формулировки спецификации. */
export const DISPUTE_REASONS = [
  {
    id: 'none',
    get label() {
      return tr(m.disputeNone);
    },
    // i18n-ignore: префикс записывается в текст спора, который читает менеджер; формулировка — из спецификации
    prefix: 'Полива не было',
  },
  {
    id: 'missing',
    get label() {
      return tr(m.disputeMissing);
    },
    // i18n-ignore: префикс записывается в текст спора, который читает менеджер; формулировка — из спецификации
    prefix: 'Полито, а в файле нет',
  },
] as const;
